import { AppData, Log } from "@lightningjs/sdk";
import { AppSetup } from "./app-setup";
import { GRIDTYPE } from "./enum";
import { fetchMultiRequest, getImage } from "./models/data-service";

type DynamicProgram = { image: string, endTime: number }

const TICK_MS = 60000

export class DynamicThumbnailService {
    static _programs = new Map<string, DynamicProgram>()
    static _subscribers = new Map<string, Set<(image: string) => void>>()
    static _inFlight = new Set<string>()
    static _intervalId: number | null = null

    static isEligible(rowType: string, w: number, h: number, grid: any): boolean {
        if (rowType !== 'live' || w <= h) return false

        const { appState } = AppData as AppSetup
        if (!appState?.getAppApiConfig()?.appConfig?.enableDynamicThumbnail) return false

        if (!grid || grid.type !== GRIDTYPE.GRID) return false

        const channelInfo: any = grid.channelInfo
        if (channelInfo?.isLivetv || channelInfo?.actionkey === 'Live') return false

        return true
    }

    static register(url: string, onImage: (image: string) => void) {
        let subs = this._subscribers.get(url)
        if (!subs) {
            subs = new Set()
            this._subscribers.set(url, subs)
        }
        subs.add(onImage)
        this._ensureTicking()

        const now = Date.now() / 1000
        const program = this._programs.get(url)
        if (program && program.endTime > now) {
            if (program.image) onImage(program.image)
        } else if (!this._inFlight.has(url)) {
            this._inFlight.add(url)
            this._fetch([url])
        }
    }

    static unregister(url: string, onImage: (image: string) => void) {
        const subs = this._subscribers.get(url)
        if (!subs) return
        subs.delete(onImage)
        if (subs.size === 0) {
            this._subscribers.delete(url)
            this._programs.delete(url)
        }
        if (this._subscribers.size === 0) this._stopTicking()
    }

    static _ensureTicking() {
        if (this._intervalId !== null) return
        this._intervalId = setInterval(() => this._tick(), TICK_MS)
    }

    static _stopTicking() {
        if (this._intervalId === null) return
        clearInterval(this._intervalId)
        this._intervalId = null
    }

    static _tick() {
        const now = Date.now() / 1000
        const due: string[] = []
        for (const url of this._subscribers.keys()) {
            const program = this._programs.get(url)
            if ((!program || program.endTime <= now) && !this._inFlight.has(url)) due.push(url)
        }
        if (due.length > 0) {
            due.forEach((url) => this._inFlight.add(url))
            this._fetch(due)
        }
    }

    static async _fetch(urls: string[]) {
        let results: any[] = []
        try {
            const response = await fetchMultiRequest(urls)
            results = Array.isArray(response.data) ? response.data : Object.values(response.data || {})
        } catch (error) {
            Log.error('DynamicThumbnailService: fetch failed', [error])
        }

        const now = Date.now() / 1000
        urls.forEach((url, i) => {
            this._inFlight.delete(url)
            const result = results[i]
            const endTime = result?.image ? result.start_time + result.duration : 0
            const program: DynamicProgram = endTime > now
                ? { image: getImage(result.image), endTime }
                : { image: '', endTime: Infinity }
            this._programs.set(url, program)

            if (program.image) {
                this._subscribers.get(url)?.forEach((cb) => cb(program.image))
            }
        })
    }
}
